/**
 * SkillboxToolbar — 技能工具箱横条组件
 *
 * 仿照 GptInpaintToolbar / MjToolbar 样式，显示在图片节点上方。
 * 三层结构：工具集 → 分组 → 技能预设
 *
 * ── 胶囊颜色方案 ──
 *   MJ Grid（红色）:        #3d1a1e
 *   MJ Upscaled（黄色）:    #3d2e12
 *   GPT Inpaint（蓝色）:    #0c2d4d
 *   技能工具箱（深红色）:   #3d1620  ← 本组件
 *
 * ── 下拉菜单配色 ──
 *   与图像/视频模型节点下方的模型选择下拉框保持一致（NodeConsole.js DropdownSelector）：
 *   背景 var(--toolbar-bg) 灰蓝色，悬停/选中用蓝色高亮框 rgba(59,130,246,*)
 *
 * ── 布局 ──
 *   [工具集1Logo + 工具集1名称] | [工具集2Logo + 工具集2名称] | [工具集3Logo + 工具集3名称] ...
 *   点击工具集按钮 → 弹出该工具集的分组和技能列表（向上展开）
 *
 * ── 显示条件 ──
 *   isNodeActive && isImageNode && !loading && hasOutput
 *
 * ── Props ──
 *   nodeId       — 当前节点 ID
 *   toolsets     — 技能工具箱数据（三层结构）
 *   onApply      — 点击技能的回调 (nodeId, preset, selectedRefImages) → 触发 AI 生成
 *   zoom         — 画布缩放倍率
 */
import React, { useState, useRef, useEffect } from 'react';
import { Sparkles } from 'lucide-react';
import ReferenceImageList from './ReferenceImageList';

// 工具集按钮
function ToolsetButton({ toolset, onClick, isActive }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      className="nodrag"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 4,
        padding: '2px 8px', borderRadius: 4, cursor: 'pointer',
        background: hover || isActive ? 'rgba(255,255,255,0.15)' : 'transparent',
        transition: 'background 0.15s',
        fontSize: 10, color: 'rgba(255,255,255,0.9)',
        whiteSpace: 'nowrap',
      }}
    >
      {toolset.logoUrl ? (
        <img
          src={toolset.logoUrl}
          alt={toolset.name}
          style={{ width: 14, height: 14, objectFit: 'contain', borderRadius: 3, flexShrink: 0 }}
          onError={e => { e.target.style.display = 'none'; }}
        />
      ) : (
        <Sparkles size={12} style={{ flexShrink: 0 }} />
      )}
      <span>{toolset.name}</span>
    </div>
  );
}

// 技能预设行 —— 静态两行显示：标题 + 描述常驻展示，不做悬停展开动画
function PresetItem({ preset, onSelect, onClose }) {
  const [isHovered, setIsHovered] = useState(false);
  return (
    <div
      onClick={() => { onSelect(preset); onClose(); }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 8px',
        marginTop: 2,
        borderRadius: 6,
        cursor: 'pointer',
        border: isHovered ? '1px solid rgba(59, 130, 246, 0.3)' : '1px solid transparent',
        background: isHovered ? 'rgba(59, 130, 246, 0.15)' : 'transparent',
        transition: 'background 0.2s, border-color 0.2s',
      }}
    >
      {preset.logoUrl ? (
        <img
          src={preset.logoUrl}
          alt={preset.name}
          style={{ width: 18, height: 18, objectFit: 'contain', borderRadius: 3, flexShrink: 0 }}
          onError={e => { e.target.style.display = 'none'; }}
        />
      ) : (
        <span style={{ fontSize: 16 }}>✨</span>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.95)', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {preset.name}
        </div>
        {preset.description && (
          <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.5)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {preset.description}
          </div>
        )}
      </div>
    </div>
  );
}

// 工具集菜单（包含分组和技能，向上展开）
function ToolsetMenu({ toolset, onSelect, onClose }) {
  return (
    <div style={{
      position: 'absolute',
      bottom: '100%',
      left: 0,
      marginBottom: 6,
      minWidth: 140,
      maxWidth: 160,
      maxHeight: 400,
      overflowY: 'auto',
      background: 'var(--toolbar-bg)',
      border: '1px solid rgba(255,255,255,0.12)',
      borderRadius: 8,
      boxShadow: '0 8px 30px rgba(0,0,0,0.5)',
      padding: 6,
      zIndex: 1200,
    }} className="custom-scrollbar nowheel">
      {toolset.groups.map((group, groupIdx) => {
        if (!group.presets || group.presets.length === 0) return null;
        return (
          <div key={group.id} style={{ marginBottom: groupIdx < toolset.groups.length - 1 ? 4 : 0 }}>
            {/* 分组标题 —— showName=false 时只保留这条分割线，logo/名字文字不渲染，
                组内技能列表照常显示（配置面板里的"眼睛"按钮控制的就是这里） */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              fontSize: 10,
              color: 'rgba(255,255,255,0.5)',
              fontWeight: 600,
              padding: '3px 6px',
              borderBottom: '1px solid rgba(255,255,255,0.12)',
            }}>
              {group.showName !== false && (
                <>
                  {group.logoUrl && (
                    <img
                      src={group.logoUrl}
                      alt={group.name}
                      style={{ width: 12, height: 12, objectFit: 'contain', borderRadius: 2, flexShrink: 0 }}
                      onError={e => { e.target.style.display = 'none'; }}
                    />
                  )}
                  <span>{group.name}</span>
                </>
              )}
            </div>
            {/* 分组下的技能列表 */}
            {group.presets.map(preset => (
              <PresetItem key={preset.id} preset={preset} onSelect={onSelect} onClose={onClose} />
            ))}
          </div>
        );
      })}
    </div>
  );
}


export default function SkillboxToolbar({ nodeId, toolsets, onApply, zoom = 1 }) {
  const [openToolsetId, setOpenToolsetId] = useState(null);
  const [refImageSelectorPreset, setRefImageSelectorPreset] = useState(null);
  const dropdownRef = useRef(null);

  // 点击外部关闭菜单
  useEffect(() => {
    if (!openToolsetId) return;
    const handleClose = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpenToolsetId(null);
      }
    };
    document.addEventListener('mousedown', handleClose, true);
    return () => document.removeEventListener('mousedown', handleClose, true);
  }, [openToolsetId]);

  if (!toolsets || toolsets.length === 0) return null;

  // 过滤出有内容的工具集（至少有一个分组且分组下有技能）
  const validToolsets = toolsets.filter(toolset =>
    toolset.groups && toolset.groups.length > 0 &&
    toolset.groups.some(group => group.presets && group.presets.length > 0)
  );

  if (validToolsets.length === 0) return null;

  const capsuleBar = {
    display: 'flex', alignItems: 'center', gap: 1,
    background: '#3d1620',
    border: '1px solid rgba(220, 38, 38, 0.35)',
    borderRadius: '50px', padding: '4px 10px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
    pointerEvents: 'auto', width: 'max-content',
    userSelect: 'none',
    backdropFilter: 'blur(8px)',
  };

  const divider = { width: 1, height: 14, background: 'rgba(255,255,255,0.2)', margin: '0 3px' };

  // 定位逻辑与 MjToolbar / GptInpaintToolbar 一致
  const titleVisualTop = 2 - 22 / zoom;
  const dynamicTop = titleVisualTop - 5 - 30;

  // 处理技能选择：如果技能有参考图，弹出选择框；否则直接应用
  const handlePresetSelect = (preset) => {
    if (preset.referenceEnabled && preset.referenceImages && preset.referenceImages.length > 0) {
      setRefImageSelectorPreset(preset);
      setOpenToolsetId(null);
    } else {
      if (onApply) onApply(nodeId, preset, []);
    }
  };

  // 处理参考图选择完成：将选中的参考图数组传递给父组件
  const handleRefImageSelect = (selectedImages) => {
    if (onApply && refImageSelectorPreset) {
      onApply(nodeId, refImageSelectorPreset, selectedImages);
    }
    setRefImageSelectorPreset(null);
  };

  return (
    <>
      <div ref={dropdownRef} className="nodrag" style={{
        position: 'absolute',
        top: dynamicTop,
        left: '50%',
        transform: `translateX(-50%) scale(${1 / zoom})`,
        transformOrigin: 'bottom center',
        zIndex: 20,
      }}>
        <div style={capsuleBar}>
          {/* 工具集按钮（并列显示，用竖线分隔） */}
          {validToolsets.map((toolset, idx) => {
            const isOpen = openToolsetId === toolset.id;
            return (
              <React.Fragment key={toolset.id}>
                {idx > 0 && <div style={divider} />}
                <div style={{ position: 'relative' }}>
                  <ToolsetButton
                    toolset={toolset}
                    onClick={() => setOpenToolsetId(isOpen ? null : toolset.id)}
                    isActive={isOpen}
                  />
                  {isOpen && (
                    <ToolsetMenu
                      toolset={toolset}
                      onSelect={handlePresetSelect}
                      onClose={() => setOpenToolsetId(null)}
                    />
                  )}
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* 参考图选择弹窗 */}
      {refImageSelectorPreset && (
        <ReferenceImageList
          referenceImages={refImageSelectorPreset.referenceImages}
          referenceLabel={refImageSelectorPreset.referenceLabel || '风格'}
          onSelect={handleRefImageSelect}
          onClose={() => setRefImageSelectorPreset(null)}
        />
      )}
    </>
  );
}
